import { useEffect } from 'react'

import { format, isValid, parse } from 'date-fns'
import { AnyAction, Dispatch } from 'redux'
import { change } from 'redux-form/immutable'

import { isLeidosUser } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { LyraCodeValidationInfo } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'

import { PAYMENT_FORM } from '../common/constants/appConstants'

// Fields that should not be reset when switching to/from Leidos.
// These are provider-level or selection-level fields, not patient-level.
const FIELDS_PRESERVED_ON_COMPANY_SWITCH = new Set([
  PAYMENT_FORM.FIELDS.PRACTICE_ID,
  PAYMENT_FORM.FIELDS.PROVIDER_LYRA_ID,
  PAYMENT_FORM.FIELDS.COMPANY,
])

export function useLeidosFieldReset(
  dispatch: Dispatch<AnyAction>,
  selectedCompany: CustomerInfo[] | undefined,
  prevSelectedCompany: CustomerInfo[] | undefined,
  isCopyFlow: boolean,
) {
  useEffect(() => {
    const prevCompany = prevSelectedCompany?.[0]?.value
    const currCompany = selectedCompany?.[0]?.value
    if (prevCompany === currCompany) return
    // Skip reset when company is first assigned from copied charge data to preserve prepopulated fields
    if (!prevCompany && isCopyFlow) return

    if (isLeidosUser(currCompany) || isLeidosUser(prevCompany)) {
      const defaultValues = PAYMENT_FORM.DEFAULT.initialValues
      Object.keys(PAYMENT_FORM.FIELDS).forEach((key) => {
        const fieldName = PAYMENT_FORM.FIELDS[key as keyof typeof PAYMENT_FORM.FIELDS]
        if (FIELDS_PRESERVED_ON_COMPANY_SWITCH.has(fieldName)) return
        dispatch(change(PAYMENT_FORM.NAME, fieldName, (defaultValues as Record<string, unknown>)[fieldName] ?? ''))
      })
    }
  }, [dispatch, isCopyFlow, prevSelectedCompany, selectedCompany])
}

/**
 * When Leidos is selected and a lyra code validation response arrives with a DOB,
 * set the hidden DOB field to the value from the response.
 * The lyra code response uses MM-dd-yyyy format; the form field expects MM/dd/yyyy.
 */
export function useLeidosLyraCodeDOB(
  dispatch: Dispatch<AnyAction>,
  selectedCompany: CustomerInfo[] | undefined,
  lyraCodeValidationInfo: LyraCodeValidationInfo,
) {
  useEffect(() => {
    const customerName = selectedCompany?.[0]?.value
    if (!isLeidosUser(customerName)) return
    const dob = lyraCodeValidationInfo?.user_info?.dob
    if (!dob) return
    // Lyra code response uses MM-dd-yyyy; the form field expects MM/dd/yyyy.
    // Parse + validate so an unexpected format or invalid date is dropped rather than silently mangled.
    const parsedDob = parse(dob, 'MM-dd-yyyy', new Date())
    if (!isValid(parsedDob)) return
    const formattedDob = format(parsedDob, 'MM/dd/yyyy')
    dispatch(change(PAYMENT_FORM.NAME, PAYMENT_FORM.FIELDS.DATE_OF_BIRTH, formattedDob))
  }, [dispatch, selectedCompany, lyraCodeValidationInfo])
}
